<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import CloseIcon from '@/assets/svg/close-24.svg?component';
import AlertCircleIcon from '@/assets/svg/conversations/alert-circle.svg?component';
import BaseModal from '@/components/Shared/Modals/BaseModal.vue';

export type DoNotContactModalVariant = 'mark' | 'remove';

// Matches the `reason` column on the backend episode row.
const REASON_MAX_LENGTH = 500;

const props = defineProps<{
  modelValue: boolean;
  variant: DoNotContactModalVariant;
  candidateName: string;
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'submit', reason: string): void;
  (e: 'cancel'): void;
}>();

const reason = ref('');

// Always start empty: a reason must never carry over to another candidate.
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) reason.value = '';
  },
);

const isMark = computed(() => props.variant === 'mark');

const title = computed(() => (isMark.value ? 'Mark as Do Not Contact?' : 'Allow contact again?'));
const accessibleTitle = computed(() =>
  props.candidateName ? `${title.value} ${props.candidateName}` : title.value,
);
const submitButtonText = computed(() =>
  isMark.value ? 'Mark as Do Not Contact' : 'Allow contact again',
);

// Closing mid-request would let the recruiter select another candidate while the
// post-mark refresh is still writing to the shared stores.
function cancel() {
  if (props.isLoading) return;
  emit('cancel');
  emit('update:modelValue', false);
}

function onUpdateModelValue(value: boolean) {
  if (props.isLoading && !value) return;
  emit('update:modelValue', value);
}

function submit() {
  if (props.isLoading) return;
  emit('submit', isMark.value ? reason.value.trim() : '');
}
</script>

<template>
  <BaseModal
    :model-value="modelValue"
    :title="title"
    max-width="600px"
    max-height="auto"
    @cancel="cancel"
    @submit="submit"
    @update:model-value="onUpdateModelValue"
  >
    <template #header>
      <div class="flex items-start justify-between gap-2">
        <h2
          :aria-label="accessibleTitle"
          class="font-serif text-2xl font-black leading-[1.2] text-highlight-1000"
        >
          {{ title }}
        </h2>
        <button
          type="button"
          aria-label="Close"
          data-test="do-not-contact-close"
          class="-m-2.5 grid h-11 w-11 shrink-0 place-content-center text-[#222222] sm:m-0 sm:h-6 sm:w-6"
          :disabled="isLoading"
          @click="cancel"
        >
          <CloseIcon class="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
    </template>

    <div v-if="isMark" data-test="do-not-contact-mark-body">
      <div class="mt-2 space-y-6 text-base leading-6 text-shade-820">
        <p>
          This will reject the candidate from all current jobs and automatically reject future
          applications. Your AI recruiters will stop screening, contacting, and scheduling
          interviews with them.
        </p>
        <p>Allowing contact again later won't restore rejected applications.</p>
      </div>

      <div
        class="mt-6 flex items-start gap-2 rounded-lg bg-tint-40 p-4 text-sm leading-[21px] text-black"
      >
        <AlertCircleIcon class="h-6 w-6 shrink-0 text-black" aria-hidden="true" />
        <p>
          <strong class="font-bold"
            >The candidate can still apply through job boards or your ATS.</strong
          >
          <br />
          New applications will go straight to Rejected.
        </p>
      </div>

      <label class="mt-6 grid gap-0.5">
        <span class="flex items-center gap-1 text-xs font-bold leading-[18px] text-shade-880">
          Reason <span class="text-sm font-normal leading-6 text-tint-400">(optional)</span>
        </span>
        <textarea
          v-model="reason"
          data-test="do-not-contact-reason"
          class="h-24 w-full resize-none rounded-md border border-tint-80 px-3 py-2 text-sm leading-[21px] text-shade-820 placeholder:text-shade-800 focus:outline-none focus:ring-2 focus:ring-highlight-500"
          :maxlength="REASON_MAX_LENGTH"
          rows="3"
          placeholder="Add a short note"
          autofocus
        />
      </label>
    </div>
    <p v-else class="mt-2 text-base leading-6 text-shade-820">
      Your AI recruiters will be allowed to screen, contact, and schedule interviews with this
      candidate again. Existing rejections won't change, but future applications will no longer be
      rejected automatically.
    </p>

    <template #actions>
      <div
        class="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end"
        :class="{ 'sm:gap-4': isMark }"
      >
        <v-btn
          :ripple="false"
          type="button"
          class="modal-button-text dnc-modal-action w-full sm:w-auto"
          variant="flat"
          :disabled="isLoading"
          @click="cancel"
          >Cancel</v-btn
        >
        <v-btn
          :ripple="false"
          type="button"
          class="modal-button-primary dnc-modal-action w-full sm:w-auto"
          variant="flat"
          :loading="isLoading"
          @click="submit"
          >{{ submitButtonText }}</v-btn
        >
      </div>
    </template>
  </BaseModal>
</template>

<style scoped>
.dnc-modal-action.v-btn {
  height: 40px !important;
  min-height: 44px;
  padding: 10px 16px !important;
  border-radius: 6px;
  letter-spacing: 0.28px !important;
}
.dnc-modal-action.modal-button-primary {
  background-color: theme('colors.black') !important;
}
.dnc-modal-action.modal-button-text {
  color: theme('colors.highlight.1000') !important;
}
@media (min-width: 640px) {
  .dnc-modal-action.v-btn {
    min-height: 0;
  }
}
</style>
